import React, { Component, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Invoice from '../../components/Invoice/Invoice';
import { Booking } from '../../types';
import '../../components/Invoice/invoice-print.css';
import { getApiUrl } from '../../config/environment';

class InvoiceErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: string }
> {
  state = { error: '' };

  static getDerivedStateFromError(error: Error) {
    return { error: error?.message || 'خطأ في عرض الفاتورة' };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="p-6 text-center text-red-600">
          <p className="font-medium mb-2">تعذر عرض الفاتورة</p>
          <p className="text-sm text-gray-600">{this.state.error}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const getPublicApiBase = () => getApiUrl();

const sleep = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const PAGE_MARGIN_MM = 4;
const A4_RENDER_WIDTH_PX = 794;
const A4_WRAPPER_WIDTH_PX = 810;

const waitForImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        })
    )
  );
};

const getSafePageBreaks = (root: HTMLElement, scaleRatio: number) => {
  const rootRect = root.getBoundingClientRect();
  const blocks = Array.from(
    root.querySelectorAll(
      [
        '.invoice-header-themed',
        '.invoice-section-block',
        '.invoice-keep-together',
        '.invoice-route-block',
        '.invoice-footer-grid',
      ].join(', ')
    )
  ) as HTMLElement[];

  return blocks
    .map((block) => {
      const rect = block.getBoundingClientRect();
      const top = (rect.top - rootRect.top) * scaleRatio;
      const bottom = (rect.bottom - rootRect.top) * scaleRatio;
      return {
        top,
        bottom,
        height: bottom - top,
      };
    })
    .filter((block) => block.height > 1)
    .sort((a, b) => a.top - b.top || b.height - a.height);
};

/**
 * Never cut through a section/item when it can move wholly to the next page.
 * For oversized sections, break only between keep-together children.
 */
const buildSliceRanges = (
  totalHeight: number,
  pageHeight: number,
  safeBreaks: Array<{ top: number; bottom: number; height: number }>
) => {
  const slices: Array<{ startY: number; endY: number }> = [];
  let startY = 0;
  const minContent = Math.min(pageHeight * 0.2, 80);

  while (startY < totalHeight) {
    const targetEnd = Math.min(startY + pageHeight, totalHeight);
    if (targetEnd >= totalHeight) {
      slices.push({ startY, endY: totalHeight });
      break;
    }

    let endY = targetEnd;
    const crossingBlocks = safeBreaks
      .filter((block) => block.top < targetEnd - 2 && block.bottom > targetEnd + 2)
      .sort((a, b) => b.height - a.height);

    let adjusted = false;

    // 1) Prefer pushing the largest crossing block wholly to the next page
    for (const block of crossingBlocks) {
      if (block.top < startY + 4) continue;
      if (block.height <= pageHeight * 0.98 && block.top - startY >= minContent) {
        endY = block.top;
        adjusted = true;
        break;
      }
    }

    // 2) Oversized section: pack complete children, then break before the next child
    if (!adjusted) {
      for (const parent of crossingBlocks) {
        if (parent.height <= pageHeight * 0.98) continue;

        const children = safeBreaks.filter(
          (block) =>
            block !== parent &&
            block.top >= parent.top - 1 &&
            block.bottom <= parent.bottom + 1 &&
            block.height < parent.height - 4
        );

        if (!children.length) continue;

        const nextChild = children.find(
          (child) => child.top < targetEnd && child.bottom > targetEnd
        );
        if (nextChild && nextChild.top - startY >= minContent) {
          endY = nextChild.top;
          adjusted = true;
          break;
        }

        const lastFullyVisible = [...children]
          .reverse()
          .find((child) => child.bottom <= targetEnd && child.top >= startY);
        const following = children.find(
          (child) => lastFullyVisible && child.top >= lastFullyVisible.bottom - 1
        );
        if (following && following.top - startY >= minContent) {
          endY = following.top;
          adjusted = true;
          break;
        }
      }
    }

    if (endY <= startY + 40) {
      endY = targetEnd;
    }

    if (totalHeight - endY < 24) {
      endY = totalHeight;
    }

    slices.push({ startY, endY });
    startY = endY;
  }

  return slices;
};

const buildPdfBlob = async (root: HTMLElement) => {
  await waitForImages(root);
  await sleep(900);

  const canvas = await html2canvas(root, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    width: root.scrollWidth,
    height: root.scrollHeight,
    windowWidth: root.scrollWidth,
    windowHeight: root.scrollHeight,
    scrollX: 0,
    scrollY: 0,
    onclone: (_doc, cloned) => {
      // اجعل شعار رؤية 2030 أبيض في PDF (html2canvas لا يعتمد على CSS filter دائماً)
      cloned.querySelectorAll('.invoice-header-ksa-logo').forEach((el) => {
        const source = el as HTMLImageElement;
        if (!source.complete || !source.naturalWidth) return;

        const tmp = document.createElement('canvas');
        tmp.width = source.naturalWidth;
        tmp.height = source.naturalHeight;
        const ctx = tmp.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(source, 0, 0);
        const imageData = ctx.getImageData(0, 0, tmp.width, tmp.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] > 10) {
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
          }
        }
        ctx.putImageData(imageData, 0, 0);
        source.src = tmp.toDataURL('image/png');
        source.style.filter = 'none';
      });
    },
  });

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  const pageWidthMm = 210;
  const pageHeightMm = 297;
  const contentWidthMm = pageWidthMm - PAGE_MARGIN_MM * 2;
  const contentHeightMm = pageHeightMm - PAGE_MARGIN_MM * 2;
  const canvasPageHeight = Math.floor((contentHeightMm / contentWidthMm) * canvas.width);
  const scaleRatio = canvas.width / root.scrollWidth;
  const safeBreaks = getSafePageBreaks(root, scaleRatio);
  const slices = buildSliceRanges(canvas.height, canvasPageHeight, safeBreaks);
  const pageImages: string[] = [];

  slices.forEach(({ startY, endY }, pageIndex) => {
    const sliceHeight = Math.min(endY - startY, canvas.height - startY);

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeight;

    const ctx = pageCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('تعذر تجهيز صفحة PDF');
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      startY,
      canvas.width,
      sliceHeight,
      0,
      0,
      pageCanvas.width,
      pageCanvas.height
    );

    const pageHeightMmActual = (sliceHeight * pageWidthMm) / canvas.width;
    const pageHeightInsideMargins = Math.min(
      contentHeightMm,
      (sliceHeight * contentWidthMm) / canvas.width
    );

    if (pageIndex > 0) {
      pdf.addPage();
    }

    const pageImage = pageCanvas.toDataURL('image/png');
    pageImages.push(pageImage);

    pdf.addImage(
      pageImage,
      'PNG',
      PAGE_MARGIN_MM,
      PAGE_MARGIN_MM,
      contentWidthMm,
      pageHeightInsideMargins
    );
  });

  // Page images double as the on-screen preview: phones (Android Chrome) can't render a PDF in an iframe
  return { blob: pdf.output('blob'), pageImages };
};

const PublicInvoicePage: React.FC = () => {
  const { vendorId = '', bookingNumber = '' } = useParams<{
    vendorId: string;
    bookingNumber: string;
  }>();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [vendorInfo, setVendorInfo] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [previewPages, setPreviewPages] = useState<string[]>([]);
  const renderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const load = async () => {
      if (!vendorId || !bookingNumber) {
        if (!alive) return;
        setError('رابط الفاتورة غير صالح');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const base = getPublicApiBase().replace(/\/$/, '');
        const url = `${base}/public/invoice/${encodeURIComponent(bookingNumber)}?vendorId=${encodeURIComponent(vendorId)}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Vendor-ID': vendorId,
          },
          signal: controller.signal,
        });

        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.success || !json?.data) {
          throw new Error(json?.message || `فشل التحميل (${res.status})`);
        }

        if (!alive) return;
        setBooking(json.data.booking);
        setVendorInfo({
          name: json.data.vendor?.name,
          companyName: json.data.vendor?.companyName,
          license: json.data.vendor?.license || {},
          contact: json.data.vendor?.contact || {},
          logo: json.data.vendor?.logo || undefined,
        });
      } catch (err: any) {
        if (!alive || err?.name === 'AbortError') return;
        setError(err?.message || 'تعذر تحميل الفاتورة');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [vendorId, bookingNumber]);

  useEffect(() => {
    if (!booking || !vendorInfo || !renderRef.current) return undefined;

    let cancelled = false;
    let objectUrl = '';

    const generatePdf = async () => {
      try {
        setIsGeneratingPdf(true);
        setError('');
        const { blob, pageImages } = await buildPdfBlob(renderRef.current!);
        if (cancelled) return;
        setPreviewPages(pageImages);
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objectUrl;
        });
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'تعذر تجهيز ملف PDF');
        }
      } finally {
        if (!cancelled) {
          setIsGeneratingPdf(false);
        }
      }
    };

    generatePdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [booking, vendorInfo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">جاري تحميل الفاتورة...</p>
      </div>
    );
  }

  if (error || !booking || !vendorInfo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4 gap-3">
        <p className="text-red-600 text-center">{error || 'الفاتورة غير موجودة'}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-500 bg-opacity-75 overflow-y-auto invoice-modal-print">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="inline-block w-full max-w-[1100px] my-8 overflow-hidden text-right align-middle bg-white shadow-xl rounded-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  فاتورة الحجز - {booking.bookingNumber}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {pdfUrl && (
                  <>
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
                      فتح PDF
                    </a>
                    <a
                      href={pdfUrl}
                      download={`invoice-${booking.bookingNumber}.pdf`}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 ml-2" />
                      تحميل PDF
                    </a>
                  </>
                )}
              </div>
            </div>

            {pdfUrl && previewPages.length > 0 ? (
              <div className="bg-gray-100 p-2 sm:p-6 flex flex-col items-center gap-3 sm:gap-6">
                {previewPages.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`فاتورة الحجز ${booking.bookingNumber} — صفحة ${i + 1}`}
                    className="block w-full max-w-[794px] h-auto bg-white shadow"
                  />
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-600">
                {isGeneratingPdf ? 'جاري تجهيز ملف PDF...' : 'جاري تحميل المعاينة...'}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: '-20000px',
          top: 0,
          width: `${A4_WRAPPER_WIDTH_PX}px`,
          zIndex: -1,
          pointerEvents: 'none',
          background: '#ffffff',
        }}
      >
        <div
          ref={renderRef}
          style={{
            width: `${A4_WRAPPER_WIDTH_PX}px`,
            background: '#ffffff',
            padding: '8px',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="invoice-pdf-source"
            style={{ width: `${A4_RENDER_WIDTH_PX}px`, margin: '0 auto', background: '#ffffff' }}
          >
            <InvoiceErrorBoundary>
              <Invoice booking={booking} vendorInfo={vendorInfo} />
            </InvoiceErrorBoundary>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicInvoicePage;
